import { NavLink, useLocation } from 'react-router-dom';
import {
  Home,
  Settings,
  Monitor,
  Network,
  Activity,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Lock,
  BookOpen,
  ExternalLink,
  KeyRound,
} from 'lucide-react';
import { useState } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import { useTenantStore } from '../../stores/tenantStore';
import { USER_TYPES } from '../../utils/constants';
import { getDocsUrl } from '../../utils/docsUrl';

// Map sidebar paths to feature keys for gating
const pathToFeatureKey: Record<string, string> = {
  '/monitoring/suricata': 'suricata',
};

interface MenuItem {
  path: string;
  label: string;
}

interface MenuGroup {
  label: string;
  icon: typeof Home;
  items: MenuItem[];
}

interface SingleMenuItem {
  label: string;
  icon: typeof Home;
  path: string;
}

type MenuEntry = MenuGroup | SingleMenuItem;

const isGroup = (entry: MenuEntry): entry is MenuGroup => 'items' in entry;

const Sidebar = () => {
  const { isFeatureEnabled } = useSettingsStore();
  const { user } = useAuthStore();
  const { getTenantId } = useTenantStore();
  const { pathname } = useLocation();
  const [openGroups, setOpenGroups] = useState<string[]>(['equipment', 'network']);
  const isSuperAdmin = user?.type === USER_TYPES.SUPER_ADMIN;
  const isChannelAdmin = user?.type === USER_TYPES.CHANNEL_ADMIN;
  // Both types have no tenant of their own, so both can be in the "nothing
  // selected" state where tenant-scoped pages have nothing to render.
  const isGlobalUserNoTenant = (isSuperAdmin || isChannelAdmin) && !getTenantId();

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label]
    );
  };

  const menuEntries: MenuEntry[] = [
    // Home - single item, no sub-menu
    {
      label: 'Home',
      icon: Home,
      path: '/dashboard',
    },
    // Equipment
    {
      label: 'Equipment',
      icon: Monitor,
      items: [
        { path: '/admin/config-templates', label: 'Config Templates' },
        { path: '/edges', label: 'Edges' },
        ...(isSuperAdmin
          ? [{ path: '/mtges', label: 'Shared Gateways' }]
          : [{ path: '/shared-gateways', label: 'Shared Gateways' }]),
        { path: '/connectors', label: 'Connectors' },
        // { path: '/devices', label: 'Devices (Legacy)' },
        // { path: '/device-groups', label: 'Device Groups (Legacy)' },
        { path: '/app-devices', label: 'Devices' },
        { path: '/app-device-groups', label: 'Device Groups' },
      ],
    },
    // Network
    {
      label: 'Network',
      icon: Network,
      items: [
        // { path: '/routing', label: 'Routing' },
        { path: '/edge-peering', label: 'Edge Peering' },
        { path: '/dns', label: 'Private DNS' },
        { path: '/routing-policies', label: 'Policy Templates' },
        // { path: '/traffic-management', label: 'Traffic Management' },
        { path: '/applications', label: 'Applications' },
      ],
    },
    // Access Platform (v3 ZTNA) — runs alongside the legacy edge product.
    // Labelled "Secure Access" and chip-marked so it never collides with the v2
    // Equipment/Network nouns (Gateways, Connectors, Applications). See
    // UI-DESIGN-v3-access.md §2 (Decision D1).
    {
      label: 'Secure Access',
      icon: KeyRound,
      items: [
        // The tenant overview is the landing page for this group (design §7.4).
        { path: '/access/dashboard', label: 'Dashboard' },
        { path: '/access/activity', label: 'Activity' },
        { path: '/access/security', label: 'Security' },
        { path: '/access/apps', label: 'Applications' },
        { path: '/access/policies', label: 'Policies' },
        { path: '/access/connectors', label: 'Connectors' },
        { path: '/access/devices', label: 'Devices' },
        // Reusable device posture requirement sets a policy picks by name (V31).
        { path: '/access/posture-profiles', label: 'Posture Profiles' },
        { path: '/access/gateways', label: 'Gateways' },
        // The SaaS catalog is a GLOBAL, SuperAdmin-owned resource (VSNP-1150) —
        // tenant admins consume it in the app-create modal but cannot edit it.
        ...(isSuperAdmin ? [{ path: '/access/saas-catalog', label: 'SaaS Catalog' }] : []),
      ],
    },
    // Monitoring
    {
      label: 'Monitoring',
      icon: Activity,
      items: [
        { path: '/monitoring/status', label: 'Device Status' },
        { path: '/monitoring/performance', label: 'Performance' },
        { path: '/monitoring/logs', label: 'Logs' },
        // { path: '/monitoring/diagnostic', label: 'Diagnostic Tools' },
        { path: '/monitoring/observability', label: 'Observability' },
        { path: '/monitoring/acl', label: 'ACL Monitoring' },
        { path: '/monitoring/suricata', label: 'Suricata IDS/IPS' },
        { path: '/monitoring/traffic', label: 'Traffic Monitoring' },
      ],
    },
    // Analytics
    {
      label: 'Analytics',
      icon: BarChart3,
      items: [
        { path: '/analytics/traffic', label: 'Traffic Analysis' },
        { path: '/analytics/reports', label: 'Reports' },
      ],
    },
    // Administration
    {
      label: 'Administration',
      icon: Settings,
      items: [
        { path: '/admin/users', label: 'Users' },
        // Identity groups for ZTNA policy "Who" — managed PER TENANT (VSNP-1161).
        // Tenant-scoped, so the no-tenant trim below hides it in All-Tenants mode.
        { path: '/admin/user-groups', label: 'User Groups' },
        // Bring-your-own IdP (Azure AD / OIDC), brokered by Keycloak — per tenant (VSNP-1164).
        { path: '/admin/identity-providers', label: 'Identity Providers' },
        // Per-tenant MDM/EDR integrations vouching device posture (VSNP-1169).
        // Lives with Identity Providers: both are "connect the tenant's own
        // external system" admin tasks, not day-to-day access operations.
        { path: '/admin/mdm-integrations', label: 'MDM Integrations' },
        // { path: '/admin/companies', label: 'Companies' },
        // { path: '/admin/licences', label: 'Licences' },
        // Channels are the SuperAdmin > ChannelAdmin > TenantAdmin middle layer.
        // Irrelevant (and 403) for a TenantAdmin, so only the two channel-aware
        // types see the link.
        ...(isSuperAdmin || isChannelAdmin ? [{ path: '/admin/channels', label: 'Channels' }] : []),
        // Tenants shows only in the "no tenant selected" state (SuperAdmin on
        // All Tenants, or a ChannelAdmin on their channel overview) — inside a
        // single-tenant view the cross-tenant list would be misleading. Both
        // manage tenants here; the list is channel-scoped for a ChannelAdmin.
        ...(isGlobalUserNoTenant ? [{ path: '/admin/tenants', label: 'Tenants' }] : []),
        { path: '/admin/audit-log', label: 'Audit Log' },
        { path: '/admin/roles', label: 'Roles & Permissions' },
        // { path: '/admin/radius', label: 'RADIUS Config' },
        { path: '/admin/system', label: 'System Settings' },
      ],
    },
  ];

  // With no tenant selected, tenant-scoped pages have nothing to render (they
  // bounce via TenantGuard), so trim the menu to what actually works.
  //
  // A SuperAdmin keeps Home + Shared Gateways + the cross-tenant admin pages.
  // A ChannelAdmin has neither a cross-tenant dashboard nor Shared Gateways
  // (SuperAdmin-only) nor the tenant admin surface — their entire no-tenant
  // world is the channel overview, which is also where they land.
  const noTenantAdminPaths = isChannelAdmin
    ? ['/admin/channels', '/admin/tenants']
    : ['/admin/users', '/admin/channels', '/admin/tenants', '/admin/audit-log', '/admin/system'];

  const visibleEntries = isGlobalUserNoTenant
    ? menuEntries
        .filter((entry) => {
          if (!isGroup(entry)) return !isChannelAdmin && entry.path === '/dashboard';
          if (entry.label === 'Equipment') return !isChannelAdmin;
          return entry.label === 'Administration';
        })
        .map((entry) => {
          if (!isGroup(entry)) return entry;
          if (entry.label === 'Administration') {
            return {
              ...entry,
              items: entry.items.filter((item) => noTenantAdminPaths.includes(item.path)),
            };
          }
          if (entry.label === 'Equipment') {
            return {
              ...entry,
              items: entry.items.filter((item) => item.path === '/mtges'),
            };
          }
          return entry;
        })
    : menuEntries;

  return (
    <div className="flex flex-col h-full bg-primary-900">
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-white/10">
        <div className="flex items-center">
          <img
            src="/images/SecurelinkLogoWhite.png"
            alt="Securelink Logo"
            className="h-10 w-auto"
          />
        </div>
      </div>

      {/* Navigation - custom scrollbar styling */}
      <nav className="flex-1 py-3 px-2 overflow-y-auto sidebar-scroll">
        <ul className="space-y-1">
          {visibleEntries.map((entry) => {
            const Icon = entry.icon;

            // Single menu item (like Home)
            if (!isGroup(entry)) {
              return (
                <li key={entry.label}>
                  <NavLink
                    to={entry.path}
                    className={({ isActive }) =>
                      `flex items-center space-x-3 px-3 py-2.5 rounded-md transition-all duration-200 ${isActive
                        ? 'bg-white/15 text-white font-heading font-semibold'
                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`
                    }
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-heading">{entry.label}</span>
                  </NavLink>
                </li>
              );
            }

            // Group with sub-items
            const isOpen = openGroups.includes(entry.label.toLowerCase());

            return (
              <li key={entry.label}>
                <button
                  onClick={() => toggleGroup(entry.label.toLowerCase())}
                  className="w-full flex items-center justify-between px-3 py-2.5 text-white/80 hover:bg-white/10 hover:text-white rounded-md transition-all duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-heading">{entry.label}</span>
                    {entry.label === 'Secure Access' && (
                      <span className="rounded-full bg-teal-400/20 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-200">
                        ZTNA
                      </span>
                    )}
                  </div>
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 opacity-60" />
                  ) : (
                    <ChevronRight className="w-4 h-4 opacity-60" />
                  )}
                </button>

                {isOpen && (
                  <ul className="mt-1 space-y-0.5">
                    {entry.items.map((item) => {
                      const featureKey = pathToFeatureKey[item.path];
                      const isDisabled = featureKey && !isFeatureEnabled(featureKey);
                      return (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            className={({ isActive }) =>
                              `flex items-center justify-between px-3 py-2 ml-8 text-sm rounded-md transition-all duration-200 ${isDisabled
                                ? 'text-white/40 hover:bg-white/5'
                                : isActive
                                  ? 'bg-white/10 text-white font-heading font-medium'
                                  : 'text-white/70 hover:bg-white/5 hover:text-white'
                              }`
                            }
                          >
                            {item.label}
                            {isDisabled && <Lock className="w-3 h-3 text-white/40" />}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Documentation & Version */}
      <div className="px-3 py-3 border-t border-white/10 space-y-2">
        <a
          href={getDocsUrl(pathname)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between px-3 py-2 rounded-md text-white/70 hover:bg-white/10 hover:text-white transition-all duration-200"
        >
          <div className="flex items-center space-x-3">
            <BookOpen className="w-5 h-5" />
            <span className="text-sm font-heading">Documentation</span>
          </div>
          <ExternalLink className="w-3.5 h-3.5 opacity-50" />
        </a>
        <p className="text-xs text-white/50 font-sans px-3">v2.0.0-beta</p>
      </div>
    </div>
  );
};

export default Sidebar;

